import { useTranslation } from "react-i18next";
import { Archive, Link2, Star } from "lucide-react";
import type { DashboardManual } from "@arc/contracts";
import { Disclosure } from "@/components/ui/disclosure";
import { ManualStateBadge } from "../../facilities/ManualStateBadge.js";
import { formatDate } from "../../../lib/date.js";
import type { ListPage } from "./useDashboardListStore.js";
import type { FacilityGroup, ListSortKey } from "./manual-list-model.js";

/**
 * The legacy `CreateManualReferenceElement` rows (ADR-034 D2): name + "Linked" badge, the state badge, the created /
 * edited line when sorted by those, the star bar on the Ratings page. Grouped by facility as `Disclosure`s (all
 * expanded, as the legacy) or one flat list. A row opens the manual's details drawer (D3); a manual under an ARCHIVED
 * facility is the legacy's labelled, non-navigating row (plan panel #6).
 */
export function ManualListRows({
  mode,
  sort,
  groups,
  flat,
  labelOf,
  onOpen,
}: {
  mode: ListPage;
  sort: ListSortKey;
  groups: FacilityGroup[] | null;
  flat: DashboardManual[];
  labelOf: (m: DashboardManual) => string;
  onOpen: (m: DashboardManual) => void;
}) {
  const { t } = useTranslation();
  const row = (m: DashboardManual, showFacility: boolean) => (
    <li key={m.id}>
      <ManualRow manual={m} mode={mode} sort={sort} facilityLabel={showFacility ? labelOf(m) : null} onOpen={onOpen} />
    </li>
  );
  if (groups !== null) {
    return (
      <div className="flex flex-col gap-2">
        {groups.map((g) => (
          <Disclosure
            key={g.facilityId}
            defaultExpanded
            title={
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{g.label}</span>
                <span className="bg-muted text-muted-foreground shrink-0 rounded-full px-1.5 text-[0.65rem]">{g.manuals.length}</span>
              </span>
            }
          >
            <ul aria-label={t("dashboard.manualsInLabel", { label: g.label })} className="m-0 flex list-none flex-col gap-1 p-0">
              {g.manuals.map((m) => row(m, false))}
            </ul>
          </Disclosure>
        ))}
      </div>
    );
  }
  return (
    <ul aria-label={t("manuals.raw")} className="m-0 flex list-none flex-col gap-1 p-0">
      {flat.map((m) => row(m, true))}
    </ul>
  );
}

function ManualRow({ manual: m, mode, sort, facilityLabel, onOpen }: { manual: DashboardManual; mode: ListPage; sort: ListSortKey; facilityLabel: string | null; onOpen: (m: DashboardManual) => void }) {
  const { t } = useTranslation();
  const body = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-medium">{m.name}</span>
          {m.parentManualId !== null ? (
            <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[0.65rem] font-medium">
              <Link2 aria-hidden="true" className="size-3" /> {t("dashboard.linked")}
            </span>
          ) : null}
          {m.facilityArchived ? (
            <span className="bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[0.65rem] font-medium">
              <Archive aria-hidden="true" className="size-3" /> {t("dashboard.facilityArchived")}
            </span>
          ) : null}
        </div>
        <p className="text-muted-foreground m-0 truncate text-xs">
          {facilityLabel !== null ? `${facilityLabel} · ` : ""}
          {m.categoryName}
          {sort === "created" ? ` · Created ${formatDate(m.createdAt)}${m.creatorUsername ? ` by ${m.creatorUsername}` : ""}` : ""}
          {sort === "edited" ? ` · Edited ${formatDate(m.updatedAt)}${m.lastUpdatedByUsername ? ` by ${m.lastUpdatedByUsername}` : ""}` : ""}
          {sort === "favourited" && m.favouritedAt ? ` · Favourited ${formatDate(m.favouritedAt)}` : ""}
        </p>
      </div>
      {mode === "ratings" ? <RatingStars rating={m.rating} /> : null}
      <ManualStateBadge state={m.state} />
    </>
  );
  const cls = "flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left";
  if (m.facilityArchived) return <div className={`${cls} opacity-70`}>{body}</div>;
  return (
    <button type="button" onClick={() => onOpen(m)} className={`${cls} hover:bg-muted focus-visible:ring-ring/50 cursor-pointer border-0 bg-transparent outline-none focus-visible:ring-2`}>
      {body}
    </button>
  );
}

/** The legacy star overlay: five outlined stars with a filled overlay clipped to `average / 5`, plus `(n)`. */
function RatingStars({ rating }: { rating: DashboardManual["rating"] }) {
  const { t } = useTranslation();
  if (rating === null) return <span className="text-muted-foreground shrink-0 text-xs">{t("dashboard.notRated")}</span>;
  const label = t("dashboard.averageOfFive", { average: rating.average, count: rating.count });
  return (
    <span className="flex shrink-0 items-center gap-1 text-xs" role="img" aria-label={label}>
      <span className="relative inline-flex" aria-hidden="true">
        <span className="text-muted-foreground flex">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="size-3.5" />
          ))}
        </span>
        <span className="text-attention absolute inset-0 flex overflow-hidden" style={{ width: `${String((rating.average / 5) * 100)}%` }}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} className="size-3.5 shrink-0 fill-current" />
          ))}
        </span>
      </span>
      <span className="text-muted-foreground tabular-nums">({rating.count})</span>
    </span>
  );
}
